import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CarouselData } from '../../../shared/dto/EntityRepository';
import {
  AlertPosition,
  AlertType,
  DisplaySize,
  ModalType,
  normalizePEInfoType,
  PEInfoType,
  SelectType,
} from '../../../shared/enum/EnumRepository';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useOptimizedImageUrl } from '../../../shared/hooks/useOptimizedImageUrl';
import { useProjectElementListStore } from '../../../shared/store/projectElementStore';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useImportStationWorkStore } from '../../../shared/store/importStationWorkStore';
import { useSettingStore } from '../../../shared/store/settingStore';
import { InputWork } from '../../../shared/component/headless/input/InputBody';
import SelectBox from '../../../shared/component/SelectBox';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { TEXTBLOCK_CAPTION_MAX_LENGTH } from '../../../shared/constants/limits';
import { getPageMessage } from '../../../shared/constants/pageMessages';
import {
  getCompactLandscapeWidth,
  getDisplaySizeValue,
  PROJECT_IMAGE_SIZE,
} from '../../../shared/util/displayImageSize';
import { filterUnsupportedImageFiles } from '../../../shared/util/imageFileFormat';
import { removeFileExtensionWithinLimit } from '../../../shared/util/imageUtils';
import { shouldShowWorkArtist } from '../../../shared/util/workArtist';
import {
  CAROUSEL_MAX_ITEMS,
  CAROUSEL_MOBILE_SLOT_ASPECT_RATIO,
  CAROUSEL_SLOT_WIDTH_PERCENT,
  formatCarouselItemInfo,
  getCarouselImportCapacity,
  getCarouselItemSource,
  getFilledCarouselItems,
  resolveCarouselSlotImage,
} from '../../../shared/util/carouselUtils';
import {
  PEHeader as DesktopHeader,
  PEImgContentWrapper as DesktopWrapper,
  PEIndicator,
  SelectBoxContainer as DesktopSelects,
  SelectBoxWrapper as DesktopSelect,
} from '../Work';
import {
  PEHeader as MobileHeader,
  PEImgContentWrapper as MobileWrapper,
  SelectBoxContainer as MobileSelects,
  SelectBoxWrapper as MobileSelect,
} from '../mobile/WorkMobile';
import importIcon from '../../../asset/icon/import.png';
import imageIcon from '../../../asset/icon/image.png';
import deleteCloseIcon from '../../../asset/icon/delete_close.png';

const LEFT_ARROW_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M14 6 L8 12 L14 18" fill="none" stroke="#111" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
)}") 8 12, w-resize`;

const RIGHT_ARROW_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M10 6 L16 12 L10 18" fill="none" stroke="#111" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
)}") 16 12, e-resize`;

const SWIPE_DISTANCE = 48;
const SLIDE_FADE_MS = 600;

interface PaintedSlideImage {
  url: string;
  alt: string;
  origin: string;
}

export interface CarouselFrameProps {
  peId: string;
  data: CarouselData;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
  isMobile: boolean;
}

const CarouselFrame: React.FC<CarouselFrameProps> = ({
  peId,
  data,
  displaySize,
  infoType,
  isMobile,
}) => {
  const { isEditMode } = useEditMode();
  const updateDisplaySize = useProjectElementListStore((s) => s.updateDisplaySize);
  const updateInfoType = useProjectElementListStore((s) => s.updateInfoType);
  const updateCarouselSlotImage = useProjectElementListStore((s) => s.updateCarouselSlotImage);
  const updateCarouselSlotCaption = useProjectElementListStore((s) => s.updateCarouselSlotCaption);
  const removeCarouselSlot = useProjectElementListStore((s) => s.removeCarouselSlot);
  const setStandardModal = useModalStore((s) => s.setStandardModal);
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);
  const clearSelectedDataList = useImportStationWorkStore((s) => s.clearSelectedDataList);
  const includeArtist = useSettingStore((s) => shouldShowWorkArtist(s.setting));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const suppressClick = useRef(false);
  const [index, setIndex] = useState(0);
  const [baseImage, setBaseImage] = useState<PaintedSlideImage | null>(null);
  const [overlayImage, setOverlayImage] = useState<PaintedSlideImage | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const baseImageRef = useRef<PaintedSlideImage | null>(null);
  const overlayKeyRef = useRef('');
  const [loadedSize, setLoadedSize] = useState<{
    url: string;
    width: number;
    height: number;
  } | null>(null);

  const storedDisplaySize = displaySize || DisplaySize.BIG;
  const effectiveDisplaySize = isMobile && storedDisplaySize !== DisplaySize.WIDE
    ? DisplaySize.BIG
    : storedDisplaySize;
  const effectiveInfoType = normalizePEInfoType(infoType);
  const slides = isEditMode ? data.items : getFilledCarouselItems(data.items);
  const slide = slides[Math.min(index, Math.max(slides.length - 1, 0))] ?? null;
  const source = slide ? getCarouselItemSource(slide) : 'empty';
  const resolved = slide ? resolveCarouselSlotImage(slide) : null;
  const optimizedUrl = useOptimizedImageUrl(resolved?.originUrl ?? '', slotRef, {
    preferThumbnail: false,
  });
  const imageUrl = optimizedUrl;
  const metaWidth = resolved?.width ?? 0;
  const metaHeight = resolved?.height ?? 0;
  const measuredSize = loadedSize?.url === (resolved?.originUrl ?? '') ? loadedSize : null;
  const imageWidth = metaWidth > 0 && metaHeight > 0 ? metaWidth : measuredSize?.width ?? 0;
  const imageHeight = metaWidth > 0 && metaHeight > 0 ? metaHeight : measuredSize?.height ?? 0;
  const imageRatio = imageWidth > 0 && imageHeight > 0 ? imageWidth / imageHeight : undefined;
  const widthVw = getDisplaySizeValue(effectiveDisplaySize, PROJECT_IMAGE_SIZE.widthVw);
  const heightVh = getDisplaySizeValue(effectiveDisplaySize, PROJECT_IMAGE_SIZE.heightVh);
  const desktopSlotWidth = !imageRatio
    ? `${widthVw}vw`
    : imageRatio > 1
      ? getCompactLandscapeWidth(effectiveDisplaySize, imageRatio, PROJECT_IMAGE_SIZE)
      : `min(${heightVh * imageRatio}vh, ${widthVw}vw)`;
  const slotWidth = isMobile
    ? `${CAROUSEL_SLOT_WIDTH_PERCENT[effectiveDisplaySize]}%`
    : desktopSlotWidth;
  const slotAspectRatio = isMobile
    ? CAROUSEL_MOBILE_SLOT_ASPECT_RATIO
    : imageRatio
      ? `${imageWidth} / ${imageHeight}`
      : 'auto';
  const slotHeight = !isMobile && !imageRatio ? `${heightVh}vh` : undefined;
  const slotMaxHeight = isMobile || !imageRatio ? undefined : `${heightVh}vh`;
  const slotMaxWidth = isMobile || !imageRatio ? undefined : `${widthVw}vw`;
  const infoText = slide ? formatCarouselItemInfo(slide, includeArtist) : '';
  const canPrev = index > 0;
  const canNext = index < slides.length - 1;
  const showInfo = effectiveInfoType !== PEInfoType.NONE && source !== 'empty';

  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(slides.length - 1, 0)));
  }, [slides.length]);

  const originUrl = resolved?.originUrl ?? '';
  const imageAlt = resolved?.alt ?? '';

  useEffect(() => {
    if (!originUrl) {
      baseImageRef.current = null;
      overlayKeyRef.current = '';
      setBaseImage(null);
      setOverlayImage(null);
      setOverlayVisible(false);
      return;
    }
    if (!imageUrl) return;

    const base = baseImageRef.current;
    if (!base || base.origin === originUrl) {
      if (base?.url === imageUrl && base.alt === imageAlt) return;
      const next = { url: imageUrl, alt: imageAlt, origin: originUrl };
      baseImageRef.current = next;
      overlayKeyRef.current = '';
      setBaseImage(next);
      setOverlayImage(null);
      setOverlayVisible(false);
      return;
    }
    if (base.url === imageUrl) return;

    const nextKey = `${originUrl}\0${imageUrl}`;
    if (overlayKeyRef.current === nextKey) return;
    overlayKeyRef.current = nextKey;
    setOverlayImage({ url: imageUrl, alt: imageAlt, origin: originUrl });
    setOverlayVisible(false);
  }, [imageAlt, imageUrl, originUrl]);

  useEffect(() => {
    if (!overlayVisible || !overlayImage) return undefined;
    const timer = window.setTimeout(() => {
      baseImageRef.current = overlayImage;
      setBaseImage(overlayImage);
      setOverlayImage(null);
      setOverlayVisible(false);
    }, SLIDE_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [overlayImage, overlayVisible]);

  const rememberImageSize = (origin: string, width: number, height: number) => {
    if (width > 0 && height > 0) {
      setLoadedSize({ url: origin, width, height });
    }
  };

  const revealOverlay = () => {
    window.requestAnimationFrame(() => setOverlayVisible(true));
  };

  const openBulkImport = () => {
    if (getCarouselImportCapacity(data.items) <= 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CAROUSEL_IMPORT_MAX', { maxCount: CAROUSEL_MAX_ITEMS }),
      });
      return;
    }
    clearSelectedDataList();
    setStandardModal({
      modalType: ModalType.IMPORT_STATION_FOR_CAROUSEL,
      title: peId,
      value: null,
      handleChange: () => {},
    });
  };

  const openSlotImport = () => {
    if (!slide) return;
    if (source === 'empty') {
      openBulkImport();
      return;
    }
    clearSelectedDataList();
    setStandardModal({
      modalType: ModalType.IMPORT_STATION_FOR_CAROUSEL_SLOT,
      title: peId,
      value: String(slide.slotIndex),
      handleChange: () => {},
    });
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !slide || !filterUnsupportedImageFiles([file]).length) return;

    const reader = new FileReader();
    reader.onload = () => {
      const localImageUrl = reader.result as string;
      const img = new Image();
      img.onload = () => {
        updateCarouselSlotImage(
          peId,
          slide.slotIndex,
          localImageUrl,
          img.width,
          img.height,
          removeFileExtensionWithinLimit(file.name, TEXTBLOCK_CAPTION_MAX_LENGTH),
        );
      };
      img.src = localImageUrl;
    };
    reader.readAsDataURL(file);
  };

  const goPrev = () => {
    if (!canPrev) return;
    setIndex((current) => current - 1);
  };
  const goNext = () => {
    if (!canNext) return;
    setIndex((current) => current + 1);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (isEditMode) return;
    touchStartX.current = event.changedTouches[0]?.clientX ?? null;
  };
  const handleTouchEnd = (event: React.TouchEvent) => {
    if (isEditMode || touchStartX.current == null) return;
    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_DISTANCE) return;
    suppressClick.current = true;
    if (delta < 0) goNext();
    else goPrev();
  };

  const handleZoneClick = (direction: 'prev' | 'next') => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    if (direction === 'prev') goPrev();
    else goNext();
  };

  const Wrapper = isMobile ? MobileWrapper : DesktopWrapper;
  const Header = isMobile ? MobileHeader : DesktopHeader;
  const Selects = isMobile ? MobileSelects : DesktopSelects;
  const Select = isMobile ? MobileSelect : DesktopSelect;

  return (
    <Wrapper>
      {isEditMode && (
        <Header>
          <Selects>
            <Select>
              <SelectBox
                value={storedDisplaySize}
                selectType={SelectType.WORK_SIZE}
                handleChange={(value) => updateDisplaySize(peId, value)}
                direction={false}
              />
            </Select>
            <Select>
              <SelectBox
                value={effectiveInfoType}
                selectType={SelectType.PE_INFO_TYPE}
                handleChange={(value) => updateInfoType(peId, value)}
                direction={false}
              />
            </Select>
          </Selects>
          <PEIndicator peId={peId}>: Carousel</PEIndicator>
        </Header>
      )}
      {slide && (
        <StageRow>
          <Spacer />
          <SlotColumn $width={slotWidth}>
            <SlideTicks aria-label={`${index + 1} / ${slides.length}`}>
              {slides.map((item, slideIndex) => (
                <SlideTick
                  key={item.id}
                  type="button"
                  $active={slideIndex === index}
                  aria-label={`${slideIndex + 1} / ${slides.length}`}
                  aria-current={slideIndex === index ? 'true' : undefined}
                  onClick={() => setIndex(slideIndex)}
                />
              ))}
            </SlideTicks>
            <SlotBox
              ref={slotRef}
              $isEmpty={source === 'empty'}
              $isEditMode={isEditMode}
              $aspectRatio={slotAspectRatio}
              $height={slotHeight}
              $maxHeight={slotMaxHeight}
              $maxWidth={slotMaxWidth}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {baseImage && (
                <SlotImage
                  src={baseImage.url}
                  alt={baseImage.alt}
                  $visible={!overlayVisible}
                  $fade={Boolean(overlayImage)}
                  onLoad={(event) => {
                    const { naturalWidth, naturalHeight } = event.currentTarget;
                    rememberImageSize(baseImage.origin, naturalWidth, naturalHeight);
                  }}
                />
              )}
              {overlayImage && (
                <SlotImage
                  key={overlayImage.origin}
                  src={overlayImage.url}
                  alt=""
                  $visible={overlayVisible}
                  onLoad={(event) => {
                    const { naturalWidth, naturalHeight } = event.currentTarget;
                    rememberImageSize(overlayImage.origin, naturalWidth, naturalHeight);
                    if (event.currentTarget.naturalWidth > 0) revealOverlay();
                  }}
                />
              )}
              <Zone
                $side="left"
                $cursor={canPrev ? LEFT_ARROW_CURSOR : 'auto'}
                aria-label="Previous slide"
                onClick={() => handleZoneClick('prev')}
              />
              <Zone $side="center" $cursor="auto" />
              <Zone
                $side="right"
                $cursor={canNext ? RIGHT_ARROW_CURSOR : 'auto'}
                aria-label="Next slide"
                onClick={() => handleZoneClick('next')}
              />
              {isEditMode && (
                <EditActions onPointerDown={(event) => event.stopPropagation()}>
                  <UniversalIconBtn
                    size="fit"
                    border={true}
                    backgroundImg={importIcon}
                    onClick={openSlotImport}
                    aria-label="Import into carousel"
                  />
                  <UniversalIconBtn
                    size="fit"
                    border={true}
                    backgroundImg={imageIcon}
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Upload carousel image"
                  />
                  {source !== 'empty' && (
                    <UniversalIconBtn
                      size="fit"
                      border={true}
                      backgroundImg={deleteCloseIcon}
                      onClick={() => removeCarouselSlot(peId, slide.slotIndex)}
                      aria-label="Remove slide"
                    />
                  )}
                  <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                  />
                </EditActions>
              )}
            </SlotBox>
          </SlotColumn>
          <Spacer />
          {isMobile && canPrev && <NavHint $side="left" aria-hidden>‹</NavHint>}
          {isMobile && canNext && <NavHint $side="right" aria-hidden>›</NavHint>}
        </StageRow>
      )}
      {slide && showInfo && (
        <CaptionRow>
          <Spacer />
          <CaptionColumn $width={slotWidth}>
            {source === 'directUpload' && isEditMode ? (
              <CaptionInput
                type="text"
                value={slide.caption ?? ''}
                placeholder={getPageMessage('DETAIL_DESCRIPTION_PLACEHOLDER')}
                maxLength={TEXTBLOCK_CAPTION_MAX_LENGTH}
                aria-label="Slide caption"
                onChange={(event) => updateCarouselSlotCaption(
                  peId,
                  slide.slotIndex,
                  event.currentTarget.value,
                )}
              />
            ) : (
              <Info title={infoText}>{infoText || '—'}</Info>
            )}
          </CaptionColumn>
          <Spacer />
        </CaptionRow>
      )}
    </Wrapper>
  );
};

const SlideTicks = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 4px;
  margin-bottom: 8px;
`;

const SlideTick = styled.button<{ $active: boolean }>`
  width: 20px;
  height: 3px;
  padding: 1px 0;
  border: 0;
  cursor: pointer;
  border-radius: 2px;
  background: ${({ $active, theme }) => (
    $active ? theme.colors.color_Gray_05 : theme.colors.color_Gray_06
  )};
  transition: height 0.3s ease;

  &:hover {
    height: 6px;
  }
`;

const [MOBILE_SLOT_WIDTH, MOBILE_SLOT_HEIGHT] = CAROUSEL_MOBILE_SLOT_ASPECT_RATIO
  .split('/')
  .map((part) => Number(part.trim()));
const MOBILE_WIDE_SLOT_CENTER = MOBILE_SLOT_HEIGHT / MOBILE_SLOT_WIDTH / 2;

const StageRow = styled.div`
  position: relative;
  container-type: inline-size;
  display: flex;
  width: 100%;
  align-items: flex-start;
`;

const Spacer = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const SlotColumn = styled.div<{ $width: string }>`
  flex: 0 0 ${({ $width }) => $width};
  width: ${({ $width }) => $width};
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: flex-basis 0.6s ease, width 0.6s ease;
`;

const CaptionRow = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  margin-top: 8px;
`;

const CaptionColumn = styled.div<{ $width: string }>`
  flex: 0 0 ${({ $width }) => $width};
  width: ${({ $width }) => $width};
  min-width: 0;
  display: flex;
  justify-content: center;
  transition: flex-basis 0.6s ease, width 0.6s ease;
`;

const SlotBox = styled.div<{
  $isEmpty: boolean;
  $isEditMode: boolean;
  $aspectRatio: string;
  $height?: string;
  $maxHeight?: string;
  $maxWidth?: string;
}>`
  position: relative;
  width: 100%;
  height: ${({ $height }) => $height ?? 'auto'};
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  max-width: ${({ $maxWidth }) => $maxWidth ?? 'none'};
  max-height: ${({ $maxHeight }) => $maxHeight ?? 'none'};
  overflow: hidden;
  transition: aspect-ratio ${SLIDE_FADE_MS}ms ease, height 0.6s ease, max-height 0.6s ease;
  background-color: ${({ $isEmpty, $isEditMode, theme }) => (
    $isEditMode && $isEmpty ? theme.colors.color_Gray_06 : 'transparent'
  )};
`;

const SlotImage = styled.img<{ $visible?: boolean; $fade?: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  pointer-events: none;
  opacity: ${({ $visible = true }) => ($visible ? 1 : 0)};
  transition: ${({ $fade = true }) => ($fade ? `opacity ${SLIDE_FADE_MS}ms ease` : 'none')};
`;

const Zone = styled.div<{ $side: 'left' | 'center' | 'right'; $cursor: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $side }) => ($side === 'left' ? '0' : $side === 'center' ? '33.333%' : '66.666%')};
  width: 33.334%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: ${({ $cursor }) => $cursor};
  z-index: 1;
`;

const NavHint = styled.span<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: calc(100cqw * ${MOBILE_WIDE_SLOT_CENTER});
  ${({ $side }) => ($side === 'left'
    ? 'left: clamp(18px, 5vw, 24px);'
    : 'right: clamp(18px, 5vw, 24px);')}
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  font-size: 22px;
  line-height: 1;
  pointer-events: none;
  z-index: 2;
`;

const EditActions = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 6px;
  z-index: 2;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const Info = styled.span`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const CaptionInput = styled(InputWork)`
  width: min(100%, 320px);
  text-align: center;
`;

export default CarouselFrame;
